// The API catalogue (LH-L, owner 2026-10-17: "where can I copy these endpoints, like Postman, UAT and production"):
// every endpoint of every lender with its UAT and production address, copy buttons, curl, a Postman collection with
// its two environments, and OpenAPI. Addresses come from Settings → Hub addresses; nothing here holds a key.
// On the API (LH-B4) the rows and the files come from the Hub (GET /admin/v1/catalogue …); on the fixtures (npm test)
// they are made here from the stores, the same way.
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Badge, EmptyState, PageHeader, SearchInput, notify } from '@/kit/ui'
import { TEST_APPLICANT } from '@/fixtures/code'
import { endpointLabel } from '@/lib/endpoints'
import { ENDPOINT_STATE_LABEL } from '@/lib/flow'
import { apiMode } from '@/lib/hubApi'
import { catalogueFileFromHub, catalogueFileFromStores, catalogueFromHub, catalogueFromStores, type CatalogueFile, type CatalogueView } from '@/lib/catalogueApi'
import { APP_KEY_HEADER, HUB_KEY_HEADER, curlFor, downloadJson, endpointBody, healthUrl, serviceBody, serviceUrl, type HubEnv } from '@/lib/urls'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import { useHubStore } from '@/store/hubStore'
import { useSettingsStore } from '@/store/settingsStore'
import { CopyIcon } from '@/screens/shared/UrlCopy'
import { EndpointStateBadge } from '@/screens/lender/flow/EndpointsSubTab'

export function CatalogueScreen() {
  const [params, setParams] = useSearchParams()
  const lenders = useHubStore((s) => s.lenders)
  const batches = useCodeStore((s) => s.batches)
  const endpoints = useFlowStore((s) => s.endpoints)
  const storeUrls = useSettingsStore((s) => s.settings.hubUrls)
  const [q, setQ] = useState('')
  const picked = params.get('lender') ?? ''

  // the Hub's catalogue (read when the screen opens), or the same made from the stores
  const local = useMemo(() => (apiMode() ? null : catalogueFromStores(lenders, batches, endpoints, storeUrls)), [lenders, batches, endpoints, storeUrls])
  const [hub, setHub] = useState<CatalogueView | null>(null)
  const [hubError, setHubError] = useState<string | null>(null)
  useEffect(() => {
    if (!apiMode()) return
    let live = true
    catalogueFromHub().then((v) => live && setHub(v), (e: Error) => live && setHubError(e.message))
    return () => { live = false }
  }, [])
  const view = local ?? hub
  const urls = view?.urls ?? storeUrls

  const shownLenders = useMemo(() => (view?.lenders ?? []).filter((l) => !picked || l.slug === picked), [view, picked])
  const rows = useMemo(() => (view?.rows ?? []).filter((r) => (!picked || r.lenderSlug === picked) && (!q || `${r.endpoint.method} ${r.endpoint.path} ${r.endpoint.title ?? ''}`.toLowerCase().includes(q.toLowerCase()))), [view, picked, q])

  const save = async (name: string, file: CatalogueFile) => {
    try {
      const value = apiMode() ? await catalogueFileFromHub(file) : catalogueFileFromStores(file, lenders, batches, endpoints, urls, TEST_APPLICANT)
      const how = await downloadJson(name, value)
      notify.success(how === 'downloaded' ? `${name} downloaded` : `${name} copied`, how === 'downloaded' ? 'Import it in Postman (File → Import).' : 'Your browser could not download, so the JSON is on the clipboard.')
    } catch (e) {
      notify.error(`${name} not made`, (e as Error).message)
    }
  }

  return (
    <>
      <PageHeader
        title="API catalogue"
        subtitle="Every endpoint of every lender with its UAT and production address. Copy one, paste the curl, or take the whole set into Postman."
        actions={
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => void save('firstfund-lender-hub.postman_collection.json', 'postman')} data-testid="dl-postman"><Download size={12} /> Postman collection</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void save('lender-hub-uat.postman_environment.json', 'postman-uat')}>Env · UAT</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void save('lender-hub-production.postman_environment.json', 'postman-production')}>Env · Production</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void save('firstfund-lender-hub.openapi.json', 'openapi')} data-testid="dl-openapi"><Download size={12} /> OpenAPI</button>
          </div>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className="input w-56" aria-label="Lender" value={picked} onChange={(e) => setParams((p) => { if (e.target.value) p.set('lender', e.target.value); else p.delete('lender'); return p })}>
          <option value="">All lenders</option>
          {(view?.lenders ?? []).map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
        </select>
        <SearchInput value={q} onChange={setQ} placeholder="Search a route or title…" className="w-64" />
        <span className="text-2xs text-muted">
          <b>UAT</b> <code>{urls.uat}</code> · <b>Production</b> <code>{urls.production}</code> · <Link className="text-primary underline" to="/settings/general">Settings → Hub addresses</Link>
        </span>
      </div>

      {!view && (hubError ? <EmptyState title="The catalogue did not load" description={hubError} /> : <p className="text-xs text-muted" data-testid="cat-loading">Reading the catalogue from the Hub…</p>)}
      {shownLenders.map((lender) => {
        const batch = lender.batch
        const mine = rows.filter((r) => r.lenderSlug === lender.slug)
        const live = batch?.status === 'live'
        const svc = (view?.serviceEndpoints ?? []).filter((e) => e.lenderSlug === lender.slug)
        return (
          <section key={lender.slug} className="card mb-3" data-testid={`cat-${lender.slug}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="section-title !mb-0">{lender.name}</h2>
              <Badge tone={lender.status === 'active' ? 'success' : lender.status === 'off' ? 'danger' : 'gray'}>{lender.status}</Badge>
              {batch ? <span className="text-2xs text-muted">batch <b>{batch.version}</b> · {batch.status.replace('_', ' ')}{live ? '' : ' · production addresses answer 404 until a batch is live'}</span> : <span className="text-2xs text-muted">no batch yet</span>}
              {batch && <span className="ml-auto text-2xs text-muted">health <code>{healthUrl(urls.uat, lender.slug)}</code> <CopyIcon text={healthUrl(urls.uat, lender.slug)} label="UAT" /><CopyIcon text={healthUrl(urls.production, lender.slug)} label="prod" disabled={!live} /></span>}
            </div>
            {!batch ? (
              <EmptyState title="No batch yet" description="Create API in the lender's 3.1 Lender APIs tab makes the first endpoint." />
            ) : mine.length === 0 ? (
              <p className="text-xs text-muted">{q ? 'No endpoint matches the search.' : 'The batch defines no endpoint yet.'}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead><tr><th>Endpoint</th><th>Outcomes</th><th>UAT address</th><th>Production address</th><th>curl</th></tr></thead>
                  <tbody>
                    {mine.map((r) => {
                      const body = r.endpoint.method === 'GET' ? null : endpointBody(TEST_APPLICANT)
                      return (
                        <tr key={r.endpoint.path} data-testid={`cat-row-${lender.slug}-${endpointLabel(r.endpoint.path)}`}>
                          <td className="whitespace-nowrap"><span className="mono text-violet-700 dark:text-violet-300">{r.endpoint.method}</span> <b className="mono">{r.endpoint.path}</b>{r.endpoint.title && <span className="block text-2xs text-muted">{r.endpoint.title}</span>}{r.endpoint.tokenSource && <Badge tone="accent" className="ml-1">token source · the Gateway calls it</Badge>}</td>
                          <td className="mono text-2xs">{r.endpoint.outcomes.join(' · ')}</td>
                          <td className="mono text-2xs"><span className="break-all">{r.uat}</span> <CopyIcon text={r.uat} label="copy" /></td>
                          <td className="mono text-2xs">{r.production ? <><span className="break-all">{r.production}</span> <CopyIcon text={r.production} label="copy" /></> : <span className="text-muted">not live</span>}</td>
                          <td className="whitespace-nowrap text-2xs">
                            <CopyIcon text={curlFor(r.endpoint.method, r.uat, body, HUB_KEY_HEADER)} label="UAT" title="curl for UAT, with the demo applicant as input" />
                            {r.production && <CopyIcon text={curlFor(r.endpoint.method, r.production, body, HUB_KEY_HEADER)} label="prod" title="curl for production" />}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {svc.length > 0 && (
              <div className="mt-3">
                <h3 className="mb-1 text-xs font-semibold">Service endpoints (what the web app calls) · <code>POST {serviceUrl(urls.production)}</code> with <code>endpoint_id</code> in the body</h3>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead><tr><th>endpoint_id</th><th>State</th><th>Workflow</th><th>UAT</th><th>Production</th><th>curl</th></tr></thead>
                    <tbody>
                      {svc.map((e) => {
                        const state = e.state
                        const prodOk = e.env === 'uat_prod' && state === 'live'
                        const curl = (env: HubEnv) => curlFor('POST', serviceUrl(urls[env]), serviceBody(e.id, TEST_APPLICANT), APP_KEY_HEADER)
                        return (
                          <tr key={e.id}>
                            <td className="mono"><b>{e.id}</b></td>
                            <td><EndpointStateBadge state={state} /></td>
                            <td className="mono text-2xs">{e.workflowName} {e.workflowVersion}</td>
                            <td className="mono text-2xs"><span className="break-all">{serviceUrl(urls.uat)}</span> <CopyIcon text={serviceUrl(urls.uat)} label="copy" /></td>
                            <td className="mono text-2xs">{prodOk ? <><span className="break-all">{serviceUrl(urls.production)}</span> <CopyIcon text={serviceUrl(urls.production)} label="copy" /></> : <span className="text-muted">{e.env === 'uat' ? 'UAT only' : ENDPOINT_STATE_LABEL[state]}</span>}</td>
                            <td className="whitespace-nowrap text-2xs"><CopyIcon text={curl('uat')} label="UAT" />{prodOk && <CopyIcon text={curl('production')} label="prod" />}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )
      })}
      <p className="text-2xs text-muted">
        Headers: a lender endpoint takes <code>{HUB_KEY_HEADER}</code>, a service endpoint takes <code>{APP_KEY_HEADER}</code> (the keys are in Settings → Key management, never in these files). A lender endpoint's body is <code>{'{ "input": applicant, "prev"?: {…} }'}</code>; its answer is the endpoint's result. UAT runs the batch with the lender's UAT variables; production answers only for a live batch.
      </p>
    </>
  )
}
